import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { redApi } from '../../services/api'
import { CalendarDays, ArrowLeft, ArrowRight } from 'lucide-react'

interface GanttItem {
  id: string
  tipo: 'appalto' | 'sal'
  immobile_id: string
  immobile_nome: string
  label: string
  start: string | null
  end: string | null
  start_eff: string | null
  end_eff: string | null
  stato: string
  progress: number
  color: string
  importo?: number
}

interface GanttData {
  items: GanttItem[]
  min_date: string | null
  max_date: string | null
}

const DAY_W = 28
const HEADER_H = 32
const ROW_H = 40

function addDays(d: string, days: number): Date {
  const date = new Date(d)
  date.setDate(date.getDate() + days)
  return date
}

function diffDays(a: string, b: string): number {
  return Math.round((new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60 * 24))
}

function formatDM(d: Date): string {
  return `${d.getDate()}/${d.getMonth() + 1}`
}

const RedGantt: React.FC = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<GanttData | null>(null)
  const [loading, setLoading] = useState(true)
  const [scrollX, setScrollX] = useState(0)
  const [filterImmobile, setFilterImmobile] = useState('')

  useEffect(() => {
    redApi.gantt.lavori()
      .then(r => setData(r.data))
      .catch(() => setData({ items: [], min_date: null, max_date: null }))
      .finally(() => setLoading(false))
  }, [])

  const items = useMemo(() => {
    if (!data) return []
    let list = data.items
    if (filterImmobile) {
      list = list.filter(i => i.immobile_nome.toLowerCase().includes(filterImmobile.toLowerCase()))
    }
    return list
  }, [data, filterImmobile])

  const { startDate, totalDays, months } = useMemo(() => {
    if (!data || !data.min_date || !data.max_date) {
      const today = new Date().toISOString().slice(0, 10)
      return { startDate: today, totalDays: 90, months: [] }
    }
    const s = new Date(data.min_date)
    s.setDate(1)
    const e = new Date(data.max_date)
    e.setMonth(e.getMonth() + 1)
    e.setDate(1)
    const total = Math.max(30, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)))
    const monthsArr: { label: string; days: number; offset: number }[] = []
    let cur = new Date(s)
    while (cur < e) {
      const monthStart = new Date(cur)
      const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)
      const days = Math.round((monthEnd.getTime() - monthStart.getTime()) / (1000 * 60 * 60 * 24))
      monthsArr.push({
        label: monthStart.toLocaleString('it-IT', { month: 'short', year: '2-digit' }),
        days,
        offset: Math.round((monthStart.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)),
      })
      cur = monthEnd
    }
    return { startDate: s.toISOString().slice(0, 10), totalDays: total, months: monthsArr }
  }, [data])

  const svgW = totalDays * DAY_W + 200
  const svgH = items.length * ROW_H + HEADER_H + 20

  if (loading) return <div className="p-8 text-gray-400">Caricamento Gantt...</div>

  return (
    <div className="p-4 space-y-4 h-[calc(100vh-64px)] flex flex-col">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalendarDays size={22} className="text-purple-600" />
          <h1 className="text-xl font-bold text-gray-900">Calendario Lavori — Gantt</h1>
          <span className="text-sm text-gray-500">{items.length} voci</span>
        </div>
        <div className="flex items-center gap-2">
          <input
            className="input text-sm py-1 px-2 w-48"
            placeholder="Filtra per immobile..."
            value={filterImmobile}
            onChange={e => setFilterImmobile(e.target.value)}
          />
          <button onClick={() => setScrollX(s => Math.max(0, s - 200))} className="btn-ghost p-1"><ArrowLeft size={16} /></button>
          <button onClick={() => setScrollX(s => s + 200)} className="btn-ghost p-1"><ArrowRight size={16} /></button>
          <button onClick={() => navigate('/red/dashboard')} className="btn-secondary text-sm">Dashboard</button>
        </div>
      </div>

      <div className="flex-1 overflow-auto border border-gray-200 rounded-xl bg-white">
        <svg width={svgW} height={svgH} style={{ minWidth: svgW }}>
          {/* Month headers */}
          {months.map((m, i) => (
            <g key={i}>
              <rect x={200 + m.offset * DAY_W} y={0} width={m.days * DAY_W} height={HEADER_H} fill={i % 2 === 0 ? '#f8fafc' : '#ffffff'} stroke="#e5e7eb" />
              <text x={200 + m.offset * DAY_W + 6} y={22} fontSize={11} fontWeight={600} fill="#6b7280">{m.label}</text>
            </g>
          ))}

          {/* Day grid lines */}
          {Array.from({ length: totalDays + 1 }).map((_, d) => {
            const x = 200 + d * DAY_W
            const date = addDays(startDate, d)
            const isWeekend = date.getDay() === 0 || date.getDay() === 6
            return (
              <g key={d}>
                {isWeekend && (
                  <rect x={x} y={HEADER_H} width={DAY_W} height={svgH - HEADER_H} fill="#f3f4f6" opacity={0.5} />
                )}
                <line x1={x} y1={HEADER_H} x2={x} y2={svgH} stroke="#e5e7eb" strokeWidth={0.5} />
                {d < totalDays && date.getDate() === 1 && (
                  <line x1={x} y1={HEADER_H} x2={x} y2={svgH} stroke="#cbd5e1" strokeWidth={1} />
                )}
              </g>
            )
          })}

          {/* Today line */}
          {(() => {
            const todayOffset = diffDays(startDate, new Date().toISOString().slice(0, 10))
            if (todayOffset < 0 || todayOffset > totalDays) return null
            const x = 200 + todayOffset * DAY_W
            return <line x1={x} y1={HEADER_H} x2={x} y2={svgH} stroke="#ef4444" strokeWidth={2} strokeDasharray="4 2" />
          })()}

          {/* Rows */}
          {items.map((item, idx) => {
            const y = HEADER_H + 4 + idx * ROW_H
            const startOffset = item.start ? diffDays(startDate, item.start) : null
            const endOffset = item.end ? diffDays(startDate, item.end) : null
            const barW = startOffset != null && endOffset != null ? Math.max(DAY_W, (endOffset - startOffset) * DAY_W) : DAY_W * 7
            const barX = startOffset != null ? 200 + startOffset * DAY_W : 200

            return (
              <g key={item.id}>
                {/* Label */}
                <rect x={0} y={y} width={200} height={ROW_H - 4} fill="#ffffff" stroke="#e5e7eb" />
                <text x={8} y={y + 16} fontSize={10} fontWeight={600} fill="#1f2937" style={{ pointerEvents: 'none' }}>
                  {item.immobile_nome}
                </text>
                <text x={8} y={y + 30} fontSize={9} fill="#6b7280" style={{ pointerEvents: 'none' }}>
                  {item.label}
                </text>

                {/* Bar */}
                <rect
                  x={barX}
                  y={y + 8}
                  width={barW}
                  height={ROW_H - 20}
                  rx={4}
                  fill={item.color}
                  opacity={0.85}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/red/${item.immobile_id}`)}
                />
                {/* Progress */}
                <rect
                  x={barX}
                  y={y + 8}
                  width={barW * (item.progress / 100)}
                  height={ROW_H - 20}
                  rx={4}
                  fill={item.color}
                  opacity={1}
                  style={{ pointerEvents: 'none' }}
                />
                <text x={barX + 6} y={y + 24} fontSize={9} fill="#ffffff" fontWeight={600} style={{ pointerEvents: 'none' }}>
                  {item.progress}%
                </text>

                {/* Effective dates overlay */}
                {item.start_eff && item.start !== item.start_eff && (
                  (() => {
                    const off = diffDays(startDate, item.start_eff!)
                    const xx = 200 + off * DAY_W
                    return <circle cx={xx} cy={y + (ROW_H - 4) / 2} r={3} fill="#10b981" />
                  })()
                )}
              </g>
            )
          })}
        </svg>
      </div>

      <div className="flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#8b5cf6' }} /> Appalto</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm" style={{ background: '#f97316' }} /> SAL</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-500" /> Inizio effettivo</span>
        <span className="flex items-center gap-1"><span className="w-4 h-0.5 bg-red-500" /> Oggi</span>
      </div>
    </div>
  )
}

export default RedGantt
